<template>
  <div class="flex-container">
    <div v-if="isLoading" class="page-loading-wrapper">
      <img src="@/assets/images/loading.gif" alt="Yükleniyor..." />
    </div>
    <template v-else>
      <div class="page-header">
        <button class="back-button" @click="router.back()">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="#242424"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <PageTop :title="`${campaignName}`" />
      </div>
      <div class="main-area">
        <div class="template-manager">
          <UmbrellaBulkTemplateManager
            :tab="'UPLOAD'"
            :action="action"
            :index="index"
            :campaignId="props.campaignId"
            :campaignName="campaignName"
            v-for="(action, index) in bulkTemplateActions"
            :key="index"
            @refreshTable="handleRefreshTable"
            @uploadSuccess="handleUploadQueuedModalOpen"
          />
        </div>

        <div class="list-area box">
          <div class="filter-area-top">
            <div class="periods">
              <div
                v-for="tab in tableTabs"
                :class="[
                  'tab table-tabs',
                  { selected: tab.key === selectedTabIndex },
                ]"
                @click="changeTab(tab.key)"
              >
                {{ tab.tabName }}
              </div>
            </div>
            <div v-if="showTopPagination" class="top-pagination">
              <Pagination
                :pageCount="bulkTable.pageCount || 0"
                :clickHandler="bulkTable.clickHandler || (() => {})"
                :value="bulkTable.pagination?.pageIndex || 0"
                :pageRange="5"
                firstLastButton="true"
                containerClass="button-group"
                prevLinkClass="border left rounded p-1"
                pageLinkClass="border rounded p-1"
                nextLinkClass="border right rounded p-1"
                :marginPages="0"
              />
            </div>
          </div>

          <div class="table-area">
            <TableForJoinCampaignBulk
              ref="bulkTable"
              :selectedTabIndex="selectedTabIndex"
              :selectedHeaderTabIndex="'UPLOAD'"
              :refreshRef="refreshRef"
              :campaignId="props.campaignId"
              :campaignName="campaignName"
              @tableStatus="tableIsLoading"
            />
          </div>
        </div>
      </div>

      <Modal
        ref="uploadQueuedModalRef"
        :withCloser="true"
        positive-action-text="Tamam"
        :showIcon="false"
        modalStyle="max-width: 460px;"
        @onPositiveActionClick="handleUploadQueuedModalClose"
      >
        <template #body>
          <div class="upload-queued-modal-body">
            <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
            <h3 class="upload-queued-title">Dosyanız sıraya alındı</h3>
            <p>Yüklediğiniz Excel dosyası sisteme işlenmek üzere sıraya alındı.</p>
            <p>Ürünleriniz eklendiğinde kampanyaya katılım durumunuzu bu ekrandan takip edebilirsiniz.</p>
          </div>
        </template>
      </Modal>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Request from '@/services/request';
import PageTop from '@/components/Page/PageTop.vue';
import TableForJoinCampaignBulk from './partials/TableForJoinCampaignBulk.vue';
import UmbrellaBulkTemplateManager from '@/components/BulkTemplateManager/UmbrellaBulkTemplateManager.vue';
import Pagination from '@/components/Table/Pagination';
import { getCampaignDetail } from './services/umbrellaCampaignService';
import { BULK_ACTIONS } from '@/utils/enum/bulkActions';
import {
  HISTORY_TAB_DOWNLOAD,
  HISTORY_TAB_UPLOAD,
  HEADER_TAB_UPLOAD,
} from '@/utils/enum/bulkActionTabs';
import {
  SELLEROFFICE_API_BASE_URL,
  UMBRELLA_CAMPAIGNS_CREATE_TEMPLATE,
} from '@/utils/enum/endpoints';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import { sendButtonClickEvent, sendTemplateDownloadFailEvent } from '@/utils/ga4/helper';
import { Modal, Icons } from '@n11/so-ui-components';

const props = defineProps({
  campaignId: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const store = useStore();
const campaignName = ref('');
const isLoading = ref(false);
const Req = new Request();
const bulkTable = ref(null);
const uploadQueuedModalRef = ref(null);
const templateDownloadPending = ref(false);

const getTemplateDetailPagePath = () =>
  (typeof window !== 'undefined'
    ? (window.location.pathname.replace(/^\/selleroffice\/v2/, '') || '/').trim()
    : '');

const tableTabs = [
  { key: HISTORY_TAB_DOWNLOAD, tabName: 'Şablon İndirme Geçmişim' },
  { key: HISTORY_TAB_UPLOAD, tabName: 'Dosya Yükleme Geçmişim' },
];
const selectedTabIndex = ref(HISTORY_TAB_UPLOAD);
const tableIsLoadingRef = ref(false);
const refreshRef = ref({
  refresh: false,
  tab: HISTORY_TAB_UPLOAD,
  headerTab: HEADER_TAB_UPLOAD,
});

const fetchCampaignName = async () => {
  if (!props.campaignId) return;
  isLoading.value = true;
  try {
    const data = await getCampaignDetail(props.campaignId);
    campaignName.value = data.name || '';
  } catch (error) {
    console.error('Error fetching campaign name:', error);
  } finally {
    isLoading.value = false;
  }
};

const bulkTemplateActions = computed(() => {
  return [
    {
      type: BULK_ACTIONS.DOWNLOAD,
      title: 'Toplu Ürün Ekleme Şablonu İndir',
      description:
        'Kampanyaya uygun ürünlerinizin bulunduğu şablonu indirerek önerilen kampanya fiyatlarını inceleyin. Satış fiyatlarını güncelleyin ve kampanyaya dahil etmek istemediğiniz ürünleri şablondan silin.',
      icon: 'bulk-download@2x.png',
      buttonText: 'Şablon İndir',
      ctaAction: async () => {
        if (templateDownloadPending.value) return;
        templateDownloadPending.value = true;
        try {
          await Req.post(
            UMBRELLA_CAMPAIGNS_CREATE_TEMPLATE.replace(
              '{campaignId}',
              props.campaignId,
            ),
            {},
            {
              baseURL: SELLEROFFICE_API_BASE_URL,
            },
          );

          store.commit('common/SET_TOASTER_DATA', {
            type: 'info',
            title: 'Şablon hazırlanıyor.',
            description: "Şablon İndirme Geçmişim'den takip edebilirsiniz.",
          });

          handleRefreshTable(HISTORY_TAB_DOWNLOAD);

          sendButtonClickEvent({
            page_path: getTemplateDetailPagePath(),
            button_name: 'Şablon İndir',
            button_type: 'Download Template',
            campaign_id: props.campaignId,
            campaign_name: campaignName.value,
            page_type: 'Template Detail',
          });
        } catch (error) {
          console.error('Şablon isteği oluşturulurken hata:', error);
          sendTemplateDownloadFailEvent({
            campaign_id: props.campaignId,
            campaign_name: campaignName.value,
            fail_reason:
              error?.data?.errors?.[0]?.message || 'Şablon indirilemedi',
          });
        } finally {
          templateDownloadPending.value = false;
        }
      },
      errors: {},
      hasShipmentTemplate: true,
      isPending: templateDownloadPending.value,
    },
    {
      type: BULK_ACTIONS.UPLOAD,
      title: 'Şablon Dosyasını Yükle',
      description:
        'Doldurduğunuz toplu ürün şablonunu yüklemek için bilgisayarınızdan seçin veya sürükleyip bırakın.',
      icon: 'bulk-upload@2x.png',
      buttonText: 'Dosya Seç',
      info: 'Dosya <span>.xls formatında</span> ve <span>en fazla 15 MB</span> boyutunda olmalıdır.',
      ctaAction: (child) => {
        if (child?.fileInput?.value) {
          child.fileInput.value.click();
        }
      },
      errors: {},
      hasShipmentTemplate: true,
    },
  ];
});

const changeTab = (key) => {
  if (selectedTabIndex.value !== key && !tableIsLoadingRef.value) {
    selectedTabIndex.value = key;
    handleRefreshTable(key);
  }
};

const tableIsLoading = (e) => {
  tableIsLoadingRef.value = e.isLoading;
};

const showTopPagination = computed(() => {
  return bulkTable.value && bulkTable.value.pageCount > 0;
});

const handleRefreshTable = (payload = null) => {
  const tab =
    payload && typeof payload === 'object'
      ? payload.tab
      : payload || selectedTabIndex.value;
  selectedTabIndex.value = tab;
  refreshRef.value = {
    tab,
    refresh: true,
    headerTab: HEADER_TAB_UPLOAD,
  };
};

const handleUploadQueuedModalOpen = () => {
  if (uploadQueuedModalRef.value) {
    uploadQueuedModalRef.value.openModal();
  }
};

const handleUploadQueuedModalClose = () => {
  if (uploadQueuedModalRef.value) {
    uploadQueuedModalRef.value.hiddenModal();
  }
};

// const handleSelectProducts = () => {
//   router.push({
//     name: 'AddProductsToCampaign',
//     params: { campaignId: props.campaignId }
//   });
// };

onMounted(() => {
  if (props.campaignId) {
    fetchCampaignName();
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.page-loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
  background-color: $colorN00;
  border-radius: 8px;

  img {
    max-width: 80px;
  }
}

.page-header {
  display: flex;
  align-items: center;
  gap: 8px;

  .back-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;

    &:hover {
      background-color: $colorN10;
    }
  }
}

.flex-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 24px;

  .main-area {
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: $colorN00;
    padding-bottom: 24px;
    border-radius: 8px;
  }

  .template-manager {
    display: flex;
    gap: 24px;
    padding: 40px 24px 16px 24px;

    > :first-child :deep(.template-inner) {
      border: 2px solid $colorM50;
    }

    > :first-child :deep(.action-description) {
      max-width: unset;
    }
  }

  .list-area {
    border-radius: 8px;
    padding: 0 24px;
  }

  .filter-area-top {
    gap: 16px;
    display: flex;
    justify-content: space-between;
    background: $colorN00;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    padding: 12px 0;

    .periods {
      display: flex;
      gap: 12px;

      .tab {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 190px;
        height: 42px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        padding: 0 12px;
        border: 0.5px solid $colorD30;

        &.table-tabs {
          width: 190px;
          padding: 0 12px;
          font-size: 13px;
          height: 32px;
        }
        &.selected {
          font-weight: 700;
          color: $colorN80;
          background: $colorM05;
          border: 2px solid $colorM50;
        }
      }
    }

    .top-pagination {
      display: flex;
      align-items: center;
    }
  }

  .table-area {
    padding: 0 0 24px 0;
  }
}

.upload-queued-modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;

  :deep(.icon) {
    width: 60px;
    height: 60px;

    svg {
      width: 60px !important;
      height: 60px !important;
    }
  }

  .upload-queued-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
  }

  p {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
  }
}
</style>
